import { GameRenderer, GameResult } from '../../interfaces/game-type-plugin.interface';
import { PollQuestion, PollDefinition, PollQuestionResponse, PollQuestionTally, PollAggregates, MockPrizeApiService } from '@kanek/shared-data-access';
import { PollRendererConfig, PollGameState } from './poll.types';
import { PollSceneComponent } from './poll-scene.component';

/**
 * Poll Game Renderer
 *
 * Platform-agnostic game logic for the poll/survey/trivia game type.
 *
 * ARCHITECTURE:
 * - HTML/3D: Glass tablet (MeshPhysicalMaterial box + ngts-html overlay)
 * - Game logic: Multi-turn state machine — play() returns null until final question,
 *   then returns GameResult (mirrors game-board's extended-play mechanic)
 * - State communication: Renderer owns state, pushes snapshots to scene component
 *   via scene.userData['setPollGameState'] callback
 *
 * MULTI-TURN FLOW (showResultsAfterSubmit: false):
 *   CTA press → record answer → if more questions: advance + return null
 *                             → if last question: evaluate prize → return GameResult
 *
 * MULTI-TURN FLOW (showResultsAfterSubmit: true):
 *   CTA press → record answer → show bar chart, return null
 *   CTA press → acknowledge results → if more questions: advance + return null
 *                                   → if last question: evaluate prize → return GameResult
 */
export class PollRenderer implements GameRenderer<PollRendererConfig> {
  private container: HTMLElement | null = null;
  private config: PollRendererConfig | null = null;
  private canvasStore: any | null = null;
  private arService?: any;

  // ── Game state (renderer is the single source of truth) ────────────────────
  private pollDefinition: PollDefinition | null = null;
  private sessionQuestions: PollQuestion[] = [];
  private currentQuestionIndex = 0;
  private sessionResponses: PollQuestionResponse[] = [];
  private currentSelectedOptionIds: string[] = [];

  /**
   * When true, the renderer is waiting for the user to acknowledge the results
   * bar chart before advancing to the next question or completing the game.
   * Only relevant when settings.showResultsAfterSubmit = true.
   */
  private waitingForResultsAcknowledgement = false;

  /**
   * Whether the "please select an answer" validation hint is currently active.
   * Stored as persistent renderer state so it survives subsequent buildAndPushState
   * calls (e.g. from the deferred setTimeout retry in setCanvasStore). Cleared
   * automatically when the user selects any option or when the game resets.
   */
  private showValidationHintFlag = false;

  // ── Optional callbacks ──────────────────────────────────────────────────────
  public onQuestionChange?: (index: number) => void;
  public onComplete?: (score: number) => void;

  // ────────────────────────────────────────────────────────────────────────────

  initialize(container: HTMLElement, arService?: any): void {
    this.container = container;
    this.arService = arService;
    container.style.position = 'relative';
    container.style.width = '100%';
    container.style.height = '100%';
  }

  async updateConfig(config: PollRendererConfig): Promise<void> {
    const previousConfig = this.config;
    this.config = config;

    // Extract poll definition from runtime flags.
    // _previewPollDefinition: set by studio-editor / preview route for Portal preview.
    // _pollDefinition:        set by play.component.ts in production (pre-fetched from CF).
    const settings = config.settings as any;
    const incomingDef: PollDefinition | null =
      settings._previewPollDefinition ?? settings._pollDefinition ?? null;
    if (incomingDef) {
      this.pollDefinition = incomingDef;
    }

    // Determine if we need to reset
    const resetRequested = settings._resetRequested;
    const prevDef = !previousConfig ? null :
      (previousConfig.settings as any)._previewPollDefinition ??
      (previousConfig.settings as any)._pollDefinition ?? null;
    const pollChanged =
      !previousConfig ||
      (previousConfig.settings as any).pollId !== (config.settings as any).pollId ||
      prevDef !== incomingDef; // catches sample → real-def transition (same pollId, different reference)

    if (resetRequested || pollChanged) {
      this.resetState();

      if (resetRequested && this.canvasStore) {
        // Trigger scene visual reset (identical pattern used by game-board & dice-roll)
        const scene = this.canvasStore.scene;
        const resetFn = scene?.userData?.['resetGame'];
        if (typeof resetFn === 'function') {
          resetFn();
        }
      }
    }

    // Push current state to scene (may be a no-op if canvasStore not yet set)
    this.buildAndPushState();
  }

  setCanvasStore(store: any): void {
    this.canvasStore = store;

    // Register callbacks the scene component will call on user interaction.
    // The scene may not be available yet (Three.js initialises it asynchronously),
    // so we register them now if possible and again in the deferred retry below.
    const registerSceneCallbacks = (scene: any) => {
      scene.userData['onOptionSelected'] = (optionId: string) => {
        this.handleOptionSelected(optionId);
      };
      scene.userData['getSelectedOptionIds'] = () =>
        [...this.currentSelectedOptionIds];
      scene.userData['requestPollStateRefresh'] = () => this.buildAndPushState();
    };

    const scene = store.scene;
    if (scene) {
      registerSceneCallbacks(scene);
    }

    // Immediate attempt: if scene is available and setPollGameState is already
    // registered (scene effect ran first) this will succeed.
    this.buildAndPushState();

    // Deferred attempt: covers the case where the scene effect (registering
    // setPollGameState) fires AFTER setCanvasStore. setTimeout(0) lets Angular
    // effects flush synchronously before this callback executes.
    setTimeout(() => {
      const deferredScene = store.scene;
      if (deferredScene && !deferredScene.userData['requestPollStateRefresh']) {
        registerSceneCallbacks(deferredScene);
      }
      this.buildAndPushState();
    }, 0);
  }

  setArService(arService: any): void {
    this.arService = arService;
  }

  getSceneComponent(): any {
    return PollSceneComponent;
  }

  /**
   * Advance the poll by one step on each CTA press.
   *
   * Returns null while the game is still in progress (more questions / pending
   * results acknowledgement), and GameResult when the user has answered all
   * questions and prize eligibility has been determined.
   */
  async play(options?: { getPrizeIndex?: () => Promise<number> }): Promise<GameResult | null> {
    if (!this.pollDefinition || this.sessionQuestions.length === 0) {
      console.error('[PollRenderer] play() called without a loaded poll definition.');
      return null;
    }

    // Phase: user is acknowledging results chart — advance to next question
    if (this.waitingForResultsAcknowledgement) {
      this.waitingForResultsAcknowledgement = false;
      return this.advanceOrComplete(options);
    }

    // Guard: user must select at least one option before submitting
    if (this.currentSelectedOptionIds.length === 0) {
      this.showValidationHintFlag = true;
      this.buildAndPushState();
      return null;
    }

    const currentQuestion = this.sessionQuestions[this.currentQuestionIndex];

    // Evaluate correctness (client-side for preview; would be server-side in full production)
    const isCorrect = this.evaluateCorrectness(currentQuestion, this.currentSelectedOptionIds);

    // Record the response
    this.sessionResponses.push({
      questionId: currentQuestion.id,
      selectedOptionIds: [...this.currentSelectedOptionIds],
      isCorrect,
    });

    // If results are enabled, show the bar chart and pause here
    if (this.config!.settings.showResultsAfterSubmit) {
      const tally = await this.fetchTallyWithUserAnswer(currentQuestion, this.currentSelectedOptionIds);
      this.buildAndPushState({
        mode: 'results',
        questionTally: tally,
        userSelectedOptionIds: [...this.currentSelectedOptionIds],
      });
      this.waitingForResultsAcknowledgement = true;
      return null;
    }

    return this.advanceOrComplete(options);
  }

  destroy(): void {
    // Deregister scene callbacks to prevent memory leaks
    if (this.canvasStore) {
      const scene = this.canvasStore.scene;
      if (scene?.userData) {
        delete scene.userData['onOptionSelected'];
        delete scene.userData['getSelectedOptionIds'];
        delete scene.userData['setPollGameState'];
      }
    }
  }

  async reset(): Promise<void> {
    this.resetState();

    if (this.canvasStore) {
      const scene = this.canvasStore.scene;
      const resetFn = scene?.userData?.['resetGame'];
      if (typeof resetFn === 'function') {
        resetFn();
      }
    }

    this.buildAndPushState();
  }

  // ── Private helpers ─────────────────────────────────────────────────────────

  private resetState(): void {
    this.currentQuestionIndex = 0;
    this.sessionResponses = [];
    this.currentSelectedOptionIds = [];
    this.waitingForResultsAcknowledgement = false;
    this.showValidationHintFlag = false;

    // Rebuild session question list from definition, applying display settings
    const settings = this.config?.settings as any;
    let questions = [...(this.pollDefinition?.questions ?? [])];

    if (settings?.randomizeQuestionOrder) {
      this.shuffleArray(questions);
    }

    if (settings?.questionsPerSession && settings.questionsPerSession > 0) {
      questions = questions.slice(0, settings.questionsPerSession);
    }

    if (settings?.randomizeOptionOrder) {
      questions = questions.map(q => ({ ...q, options: this.shuffleArray([...q.options]) }));
    }

    this.sessionQuestions = questions;
  }

  /** Fisher-Yates in-place shuffle. Returns the same array for chaining convenience. */
  private shuffleArray<T>(arr: T[]): T[] {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  /** Called by scene HTML overlay when the user taps an answer option */
  private handleOptionSelected(optionId: string): void {
    const question = this.sessionQuestions[this.currentQuestionIndex];
    if (!question) return;

    if (question.allowMultiple) {
      // Multi-select: toggle the tapped option
      const idx = this.currentSelectedOptionIds.indexOf(optionId);
      if (idx >= 0) {
        this.currentSelectedOptionIds.splice(idx, 1);
      } else {
        this.currentSelectedOptionIds.push(optionId);
      }
    } else {
      // Single-select: replace selection, or deselect if tapping the same option
      this.currentSelectedOptionIds = this.currentSelectedOptionIds.includes(optionId)
        ? []
        : [optionId];
    }

    // Reflect selection change in scene (clears validation hint)
    this.showValidationHintFlag = false;
    this.buildAndPushState({
      userSelectedOptionIds: [...this.currentSelectedOptionIds],
    });
  }

  /**
   * Move to the next question, or end the game if all have been answered.
   * Shared by both showResults and non-showResults paths.
   */
  private async advanceOrComplete(
    options?: { getPrizeIndex?: () => Promise<number> },
  ): Promise<GameResult | null> {
    const hasMore = this.currentQuestionIndex < this.sessionQuestions.length - 1;

    if (hasMore) {
      this.currentQuestionIndex++;
      this.currentSelectedOptionIds = [];
      this.showValidationHintFlag = false;
      this.buildAndPushState({
        mode: 'question',
        currentQuestionIndex: this.currentQuestionIndex,
        userSelectedOptionIds: [],
      });
      this.onQuestionChange?.(this.currentQuestionIndex);
      return null; // Multi-turn: game not complete yet
    }

    // All questions answered — determine prize eligibility
    return this.buildFinalResult(options);
  }

  private async buildFinalResult(
    options?: { getPrizeIndex?: () => Promise<number> },
  ): Promise<GameResult> {
    const settings = this.config!.settings;
    const prizes = this.config!.items;
    let prizeEligible = false;
    let score = 0;

    switch (settings.rewardMode) {
      case 'always':
        prizeEligible = true;
        score = 100;
        break;

      case 'on-correct': {
        const correctCount = this.sessionResponses.filter(
          (r) => r.isCorrect === true,
        ).length;
        prizeEligible = correctCount === this.sessionResponses.length;
        score = (correctCount / this.sessionResponses.length) * 100;
        break;
      }

      case 'on-score-threshold': {
        const correctCount = this.sessionResponses.filter(
          (r) => r.isCorrect === true,
        ).length;
        score = (correctCount / this.sessionResponses.length) * 100;
        prizeEligible = score >= (settings.scoreThreshold ?? 100);
        break;
      }
    }

    this.buildAndPushState({ mode: 'complete' });
    this.onComplete?.(score);

    if (!prizeEligible) {
      return { isWinner: false, metadata: { score, responses: [...this.sessionResponses] } };
    }

    // Preview mode: use MockPrizeApiService directly if no callback provided
    if (!options?.getPrizeIndex) {
      const previewPrizes = (this.config!.settings as any)._previewPrizes;
      if (previewPrizes?.length > 0) {
        const idx = MockPrizeApiService.selectPrizeIndex(previewPrizes);
        const prize = previewPrizes[idx];
        return {
          isWinner: true,
          prize: { id: prize.id ?? 'preview', name: prize.name, type: prize.type ?? 'physical' },
          metadata: { score, responses: [...this.sessionResponses] },
        };
      }
      return { isWinner: true, metadata: { score, responses: [...this.sessionResponses] } };
    }

    // Production: call the platform's prize API callback
    const prizeIndex = await options.getPrizeIndex();
    const prize = prizes[prizeIndex];
    return {
      isWinner: true,
      prize: prize
        ? { id: prize.id ?? String(prizeIndex), name: prize.name, type: prize.type ?? 'physical' }
        : undefined,
      metadata: { score, responses: [...this.sessionResponses] },
    };
  }

  private evaluateCorrectness(
    question: PollQuestion,
    selectedOptionIds: string[],
  ): boolean {
    if (this.config?.settings.rewardMode === 'always') return true;
    // At least one selected option must be marked isCorrect
    return selectedOptionIds.some(
      (id) => question.options.find((o) => o.id === id)?.isCorrect === true,
    );
  }

  /**
   * Fetch the tally to display in results mode.
   * - Production (Experience app): calls `_getPollAggregates` CF and merges the
   *   user's current (unsubmitted) answer so they see a preview of what the
   *   results look like with their vote included.
   * - Preview (Portal studio): falls back to plausible mock data.
   */
  private async fetchTallyWithUserAnswer(
    question: PollQuestion,
    selectedOptionIds: string[],
  ): Promise<PollQuestionTally> {
    const settings = this.config!.settings as any;
    const getPollAggregates = settings._getPollAggregates as
      | ((pollId: string) => Promise<{ aggregates: PollAggregates }>)
      | undefined;

    if (getPollAggregates) {
      try {
        const response = await getPollAggregates(settings.pollId);
        return this.mergeLiveAggregatesWithUserAnswer(response.aggregates, question, selectedOptionIds);
      } catch (err) {
        console.error('[PollRenderer] Failed to fetch live aggregates, using preview data:', err);
      }
    }

    return this.computePreviewAggregates(question, selectedOptionIds);
  }

  /**
   * Merge real DB aggregate data with the user's current (unsubmitted) answer
   * so the chart shows "results so far + your vote".
   */
  private mergeLiveAggregatesWithUserAnswer(
    aggregates: PollAggregates,
    question: PollQuestion,
    selectedOptionIds: string[],
  ): PollQuestionTally {
    const questionData = aggregates.questions.find(
      (q) => q.questionId === question.id,
    );

    if (!questionData) {
      // No aggregate data for this question yet — seed from the user's answer.
      // totalResponses = 1 (this respondent); percentage = count/1*100.
      return {
        questionId: question.id,
        questionText: question.text,
        totalResponses: 1,
        options: question.options.map((opt) => {
          const count = selectedOptionIds.includes(opt.id) ? 1 : 0;
          return { optionId: opt.id, optionText: opt.text, count, percentage: count * 100 };
        }),
      };
    }

    const mergedTotal = questionData.totalResponses + 1;
    const mergedOptions = questionData.options.map((opt) => {
      const newCount = opt.count + (selectedOptionIds.includes(opt.optionId) ? 1 : 0);
      return {
        optionId: opt.optionId,
        optionText: opt.optionText,
        count: newCount,
        percentage: Math.round((newCount / mergedTotal) * 100),
      };
    });

    return {
      questionId: question.id,
      questionText: question.text,
      totalResponses: mergedTotal,
      options: mergedOptions,
    };
  }

  /**
   * Compute plausible-looking preview aggregate tallies for display in results
   * mode without requiring a real Firestore read.
   */
  private computePreviewAggregates(
    question: PollQuestion,
    selectedOptionIds: string[],
  ): PollQuestionTally {
    const optionCount = question.options.length;
    const baseTotal = 48;
    const evenShare = Math.floor(baseTotal / optionCount);

    const rawOptions = question.options.map((opt, i) => {
      // First option gets a slight bump, last gets slightly fewer (realistic skew)
      const variation = i === 0 ? 10 : i === optionCount - 1 ? -8 : 0;
      const userBonus = selectedOptionIds.includes(opt.id) ? 4 : 0;
      const count = Math.max(1, evenShare + variation + userBonus);
      return { optionId: opt.id, optionText: opt.text, count };
    });

    const totalCount = rawOptions.reduce((sum, o) => sum + o.count, 0);

    return {
      questionId: question.id,
      questionText: question.text,
      totalResponses: totalCount,
      options: rawOptions.map((o) => ({
        ...o,
        percentage: Math.round((o.count / totalCount) * 100),
      })),
    };
  }

  /**
   * Build a complete PollGameState snapshot and push it to the scene component
   * via the registered `setPollGameState` callback in scene.userData.
   *
   * Accepts optional partial overrides to merge on top of the current state.
   */
  private buildAndPushState(overrides?: Partial<PollGameState>): void {
    const scene = this.canvasStore?.scene;
    if (!scene) return;

    const state: PollGameState = {
      mode: this.waitingForResultsAcknowledgement ? 'results' : 'question',
      currentQuestionIndex: this.currentQuestionIndex,
      totalQuestions: this.sessionQuestions.length,
      questions: this.sessionQuestions,
      pollDefinition: this.pollDefinition,
      userSelectedOptionIds: [...this.currentSelectedOptionIds],
      questionTally: undefined,
      // Read from persistent flag so hint survives subsequent state rebuilds
      showValidationHint: this.showValidationHintFlag,
      ...overrides,
    };

    const setter = scene.userData['setPollGameState'];
    if (typeof setter === 'function') {
      setter(state);
    }

    // Drive the game engine's CTA button label to reflect the current action
    const setCTALabel = scene.userData['setCTALabel'];
    if (typeof setCTALabel === 'function') {
      let label: string | null;
      if (state.mode === 'complete') {
        label = null; // Game engine will hide the button
      } else if (state.mode === 'results') {
        const isLastQuestion =
          state.currentQuestionIndex >= state.totalQuestions - 1;
        label = isLastQuestion ? 'Done' : 'Next';
      } else {
        label = 'Submit';
      }
      setCTALabel(label);
    }
  }
}
